import { useState } from 'react'
import { useA2FSettings } from '../../context/A2FSettingsContext'
import { ARKIT_BLENDSHAPES } from '../../constants/arkitBlendshapes'
import { FACE_PARAMS } from '../../constants/faceParams'
import { EMOTION_NAMES } from '../../constants/emotions'
import styles from './A2FSettingsSidebar.module.css'

interface Props {
  onClose: () => void
  onApply: () => void
}

function MapSection({ title, data, allKeys, onAdd, onRemove, onChange }: {
  title: string
  data: Record<string, number>
  allKeys: string[]
  onAdd: (key: string, val: number) => void
  onRemove: (key: string) => void
  onChange: (key: string, val: number) => void
}) {
  const [selectedKey, setSelectedKey] = useState(allKeys[0] || '')
  const [newVal, setNewVal] = useState('1.0')
  const [open, setOpen] = useState(false)

  function handleAdd() {
    const v = parseFloat(newVal)
    if (!selectedKey || isNaN(v)) return
    onAdd(selectedKey, v)
  }

  return (
    <div className={styles.section}>
      <button className={`${styles.sectionToggle} ${open ? styles.expanded : ''}`} onClick={() => setOpen(v => !v)}>
        <span>{title}</span>
        <span className={styles.arrow}>▶</span>
      </button>
      {open && (
        <div className={styles.sectionBody}>
          {Object.entries(data).map(([key, val]) => (
            <div key={key} className={styles.mapRow}>
              <span className={styles.mapKey} title={key}>{key}</span>
              <input
                type="number"
                step="0.01"
                value={val}
                onChange={e => onChange(key, parseFloat(e.target.value))}
                className={styles.numInput}
              />
              <button className={styles.deleteBtn} onClick={() => onRemove(key)}>×</button>
            </div>
          ))}
          <div className={styles.addRow}>
            <select value={selectedKey} onChange={e => setSelectedKey(e.target.value)} className={styles.select}>
              {allKeys.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <input
              type="number"
              step="0.01"
              value={newVal}
              onChange={e => setNewVal(e.target.value)}
              className={styles.numInputSmall}
            />
            <button className={styles.addBtn} onClick={handleAdd}>+</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function A2FSettingsSidebar({ onClose, onApply }: Props) {
  const {
    blendshapeMultipliers, blendshapeOffsets, faceParams, initialEmotions, scalars, touchedCheckboxes,
    setBlendshapeMultiplier, removeBlendshapeMultiplier,
    setBlendshapeOffset, removeBlendshapeOffset,
    setFaceParam, removeFaceParam,
    setEmotion, removeEmotion,
    setScalar, touchCheckbox
  } = useA2FSettings()

  const numField = (key: string, label: string, step = '0.01') => (
    <div className={styles.scalarRow} key={key}>
      <label className={styles.scalarLabel}>{label}</label>
      <input
        type="number"
        step={step}
        value={scalars[key] ?? ''}
        onChange={e => setScalar(key, e.target.value)}
        className={styles.numInput}
      />
    </div>
  )

  const checkField = (key: string, label: string) => (
    <div className={styles.scalarRow} key={key}>
      <label className={styles.scalarLabel}>{label}</label>
      <input
        type="checkbox"
        checked={!!scalars[key]}
        onChange={e => { touchCheckbox(key); setScalar(key, e.target.checked ? 1 : 0) }}
        style={{ accentColor: '#e94560' }}
      />
    </div>
  )

  // suppress unused variable warning for touchedCheckboxes
  void touchedCheckboxes

  return (
    <aside className={styles.sidebar}>
      <div className={styles.header}>
        A2F Settings
        <button className={styles.closeBtn} onClick={onClose}>×</button>
      </div>
      <div className={styles.content}>
        <MapSection
          title="Blendshape Multipliers"
          data={blendshapeMultipliers}
          allKeys={ARKIT_BLENDSHAPES}
          onAdd={setBlendshapeMultiplier}
          onRemove={removeBlendshapeMultiplier}
          onChange={setBlendshapeMultiplier}
        />
        <MapSection
          title="Blendshape Offsets"
          data={blendshapeOffsets}
          allKeys={ARKIT_BLENDSHAPES}
          onAdd={setBlendshapeOffset}
          onRemove={removeBlendshapeOffset}
          onChange={setBlendshapeOffset}
        />
        <MapSection
          title="Face Params"
          data={faceParams}
          allKeys={FACE_PARAMS}
          onAdd={setFaceParam}
          onRemove={removeFaceParam}
          onChange={setFaceParam}
        />
        <MapSection
          title="Initial Emotions"
          data={initialEmotions}
          allKeys={EMOTION_NAMES}
          onAdd={setEmotion}
          onRemove={removeEmotion}
          onChange={setEmotion}
        />

        <div className={styles.section}>
          <button className={`${styles.sectionToggle}`} style={{ cursor: 'default', fontWeight: 600 }}>
            Scalars
          </button>
          <div className={styles.sectionBody} style={{ display: 'block' }}>
            {numField('emotionContrast', 'Emotion Contrast')}
            {numField('liveBlendCoef', 'Live Blend Coef')}
            {numField('emotionStrength', 'Emotion Strength')}
            {numField('preferredEmotionStrength', 'Preferred Emotion Strength')}
            {numField('maxEmotions', 'Max Emotions', '1')}
            {numField('liveTransitionTime', 'Live Transition Time')}
            {checkField('enableClampingBsWeight', 'Enable Clamping BS Weight')}
            {checkField('enablePreferredEmotion', 'Enable Preferred Emotion')}
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <button className={styles.applyBtn} onClick={onApply}>Apply</button>
      </div>
    </aside>
  )
}
